import React, { useEffect, useState } from 'react';
import classNames from 'classnames';
import PropTypes from 'prop-types';

import { ChevronDownIcon12 } from '../RacingThemeSearch/RacingThemeSearchIcons';
import RacingThemeSearchFilterOption from './RacingThemeSearchFilterOption';
import RacingThemeSearchPriceRange from './RacingThemeSearchPriceRange';
import RacingThemeSearchRatingRow from './RacingThemeSearchRatingRow';
import styles from './RacingThemeSearchFilterGroup.module.css';

const checkboxOptionShape = PropTypes.shape({
  id: PropTypes.string.isRequired,
  label: PropTypes.string.isRequired,
  count: PropTypes.number,
  checked: PropTypes.bool,
  children: PropTypes.array,
});

const ratingOptionShape = PropTypes.shape({
  id: PropTypes.string.isRequired,
  filledStars: PropTypes.number.isRequired,
  checked: PropTypes.bool,
});

const shouldAutoExpandCategory = option =>
  (option.children || []).some(child => child.checked || shouldAutoExpandCategory(child));

/**
 * Nested category option row with optional expand control.
 */
const CategoryOptionNode = props => {
  const { option, groupId, depth = 0, onOptionChange } = props;
  const hasChildren = Array.isArray(option.children) && option.children.length > 0;
  const [isOpen, setIsOpen] = useState(() => shouldAutoExpandCategory(option));
  const hasSelectedDescendant = shouldAutoExpandCategory(option);

  useEffect(() => {
    if (hasSelectedDescendant) {
      setIsOpen(true);
    }
  }, [hasSelectedDescendant]);

  return (
    <div className={styles.categoryNode} style={{ paddingLeft: depth * 12 }}>
      <div className={styles.categoryRow}>
        <RacingThemeSearchFilterOption
          label={option.label}
          count={option.count}
          checked={!!option.checked}
          name={`${groupId}-filter`}
          onChange={checked => {
            if (typeof onOptionChange === 'function') {
              onOptionChange({
                groupId,
                optionId: option.id,
                checked,
                option,
              });
            }
          }}
        />
        {hasChildren ? (
          <button
            type="button"
            className={classNames(styles.categoryExpandBtn, isOpen && styles.categoryExpandBtnOpen)}
            aria-expanded={isOpen}
            aria-label={isOpen ? `Collapse ${option.label}` : `Expand ${option.label}`}
            onClick={() => setIsOpen(open => !open)}
          >
            <ChevronDownIcon12 />
          </button>
        ) : null}
      </div>
      {hasChildren && isOpen
        ? option.children.map(child => (
            <CategoryOptionNode
              key={child.id}
              option={child}
              groupId={groupId}
              depth={depth + 1}
              onOptionChange={onOptionChange}
            />
          ))
        : null}
    </div>
  );
};

CategoryOptionNode.propTypes = {
  option: checkboxOptionShape.isRequired,
  groupId: PropTypes.string,
  depth: PropTypes.number,
  onOptionChange: PropTypes.func,
};

/**
 * Search filter accordion group.
 */
const RacingThemeSearchFilterGroup = props => {
  const {
    id,
    title,
    type,
    schemaType,
    options = [],
    minValue,
    maxValue,
    onOptionChange,
    onPriceCommit,
  } = props;

  const [isExpanded, setIsExpanded] = useState(true);
  const isCategoryTree = schemaType === 'category';

  return (
    <div className={styles.filterGroup}>
      <button
        type="button"
        className={styles.filterGroupHead}
        aria-expanded={isExpanded}
        onClick={() => setIsExpanded(open => !open)}
      >
        <span className={styles.filterGroupTitle}>{title}</span>
        <span
          className={classNames(styles.filterGroupChevron, isExpanded && styles.filterGroupChevronOpen)}
        >
          <ChevronDownIcon12 />
        </span>
      </button>
      {isExpanded ? (
        <div className={styles.filterGroupBody}>
          {type === 'price' ? (
            <RacingThemeSearchPriceRange
              minValue={minValue}
              maxValue={maxValue}
              onCommit={values => {
                if (typeof onPriceCommit === 'function') {
                  onPriceCommit({ groupId: id, ...values });
                }
              }}
            />
          ) : null}
          {type === 'checkbox' && isCategoryTree
            ? options.map(option => (
                <CategoryOptionNode
                  key={option.id}
                  option={option}
                  groupId={id}
                  onOptionChange={onOptionChange}
                />
              ))
            : null}
          {type === 'checkbox' && !isCategoryTree
            ? options.map(option => (
                <RacingThemeSearchFilterOption
                  key={option.id}
                  label={option.label}
                  count={option.count}
                  checked={!!option.checked}
                  name={`${id}-filter`}
                  onChange={checked => {
                    if (typeof onOptionChange === 'function') {
                      onOptionChange({
                        groupId: id,
                        optionId: option.id,
                        checked,
                        option,
                      });
                    }
                  }}
                />
              ))
            : null}
          {type === 'rating'
            ? options.map(option => (
                <RacingThemeSearchRatingRow
                  key={option.id}
                  filledStars={option.filledStars}
                  checked={option.checked}
                />
              ))
            : null}
        </div>
      ) : null}
    </div>
  );
};

RacingThemeSearchFilterGroup.propTypes = {
  id: PropTypes.string,
  title: PropTypes.string.isRequired,
  type: PropTypes.oneOf(['checkbox', 'price', 'rating']).isRequired,
  schemaType: PropTypes.string,
  options: PropTypes.arrayOf(PropTypes.oneOfType([checkboxOptionShape, ratingOptionShape])),
  minValue: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  maxValue: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onOptionChange: PropTypes.func,
  onPriceCommit: PropTypes.func,
};

export default RacingThemeSearchFilterGroup;
